import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { reaction } from "mobx";
import dayjs from "dayjs";
import {
  Box,
  CircularProgress,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { Download as DownloadIcon, ExpandLess, ExpandMore } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import FormTextField from "../../components/common/form/FormTextField";
import AppDropDown from "../../components/common/filter/AppDropDown";
import AppButton from "../../components/common/buttons/AppButton";
import CustomTypography from "../../components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";
import { SortType } from "../../../models/SortType";
import { PAYMENT_LINK_STATUS_OPTIONS } from "../../../models/PaymentLinks/PaymentLinks";
import { PaymentLinkStore } from "../../../stores/PaymentLinkStore";
import PaymentLinksTable from "../../components/order/PaymentLinksTable";

const PaymentLinksListing: React.FC = () => {
  const theme = useTheme();
  const { userStore } = useStores();
  const location = useLocation();
  const store = useRef(
    new PaymentLinkStore(new URLSearchParams(location.search)),
  ).current;
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();

  const hasPermission = userStore.hasPermission(EmployeeRoles.VIEW_ORDER);

  useEffect(() => {
    if (!hasPermission) return;

    const disposeFetch = reaction(
      () => JSON.stringify(store.params),
      () => {
        store.getPaymentLinks().catch((error: any) => {
          showError(
            "Error",
            error?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
        });
      },
      { fireImmediately: true },
    );

    return () => disposeFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  useEffect(() => {
    const disposeUrlSync = reaction(
      () => JSON.stringify(store.params),
      () => {
        const query = new URLSearchParams();
        Object.entries(store.params).forEach(([key, value]) => {
          if (value !== undefined && value !== "") {
            query.set(key, value.toString());
          }
        });
        const queryString = query.toString();
        if (queryString) {
          navigate({ search: queryString }, { replace: true });
        }
      },
    );

    return () => disposeUrlSync();
  }, [navigate, store]);

  const handleExport = async () => {
    try {
      const response = await store.exportPaymentLinksCSV();

      if (!response.data) return;
      const downloadUrl = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = response.filename || "payment-links.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      showSuccess("Payment links CSV downloaded successfully.");
    } catch (error: any) {
      console.error("Failed to export payment links CSV", error);
      showError(
        "Error",
        error?.response?.data?.message ||
          "Failed to download payment links CSV.",
      );
    }
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Paper>
        {!hasPermission && <PermissionBlockLabel />}
        {hasPermission && (
          <Box
            sx={{
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
              bgcolor: "#FFFFFF",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              paddingX: 3,
              paddingY: 2,
              minHeight: { xs: "60vh", md: window.innerHeight - 110 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <CustomTypography variant="heading3" fontWeight={700}>
                Payment Links
              </CustomTypography>
              <AppButton
                variant="primary"
                startIcon={<DownloadIcon />}
                label={store.isExporting ? "Exporting..." : "Export CSV"}
                onClick={handleExport}
                disabled={store.isExporting}
                customStyles={{ width: "fit-content", whiteSpace: "nowrap" }}
              />
            </Box>

            <Paper
              variant="outlined"
              sx={{
                mt: 3,
                borderRadius: 2,
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={store.toggleSearchExpanded}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 0.5,
                  cursor: "pointer",
                  borderBottom: store.isSearchExpanded ? "1px solid" : "none",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <CustomTypography variant="heading6">Search</CustomTypography>
                <IconButton size="small">
                  {store.isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={store.isSearchExpanded} timeout="auto" unmountOnExit>
                <Grid2 container spacing={2} columns={12} padding={2}>
                  <Grid2
                    size={{ xs: 12, sm: 9, md: 11, lg: 11 }}
                    sx={{ padding: 0 }}
                  >
                    <FormTextField
                      marginBottom={0}
                      id="payment-links-order-search"
                      placeholder="Search by Order ID"
                      type="search"
                      variant="outlined"
                      value={store.searchTerm}
                      onChange={(_, value) => store.handleSearchBar(value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          store.handleSearchSubmit();
                        }
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 3, md: 1, lg: 1 }}>
                    <AppButton
                      variant="primary"
                      label="Search"
                      onClick={store.handleSearchSubmit}
                      customStyles={{ width: "100%", height: 40 }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                    <DateRangePickerNew
                      placeholder="Created Date Range"
                      startDate={
                        store.draftStartDate
                          ? dayjs(store.draftStartDate)
                          : null
                      }
                      endDate={
                        store.draftEndDate ? dayjs(store.draftEndDate) : null
                      }
                      onRangeSelect={(range) => {
                        store.setDraftDateRange(
                          range.startDate
                            ? range.startDate.format("YYYY-MM-DD")
                            : undefined,
                          range.endDate
                            ? range.endDate.format("YYYY-MM-DD")
                            : undefined,
                        );
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                    <AppDropDown
                      title=""
                      isMultiSelect={true}
                      showPlaceholder={true}
                      placeholderText="Payment Status"
                      valuesList={store.draftStatus.map((item) => item.id)}
                      list={PAYMENT_LINK_STATUS_OPTIONS}
                      fontSize="12px"
                      onMultiSelectionChange={(ids) => {
                        store.setDraftStatus(
                          PAYMENT_LINK_STATUS_OPTIONS.filter((item) =>
                            ids.includes(item.id),
                          ),
                        );
                      }}
                    />
                  </Grid2>
                  <Grid2
                    size={{ xs: 12, sm: 6, md: 4, lg: 6 }}
                    sx={{ marginLeft: "auto" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        justifyContent: "flex-end",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <AppButton
                        variant="transparentPrimary"
                        label="Clear"
                        onClick={store.resetFilters}
                        customStyles={{
                          width: "fit-content",
                          whiteSpace: "nowrap",
                        }}
                      />
                      <AppButton
                        variant="primary"
                        label="Apply"
                        onClick={store.handleApply}
                        customStyles={{
                          width: "fit-content",
                          whiteSpace: "nowrap",
                        }}
                      />
                    </Box>
                  </Grid2>
                </Grid2>
              </Collapse>
            </Paper>

            {store.isLoading && (
              <Box
                sx={{
                  minHeight: "50vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {!store.isLoading && (
              <Box sx={{ mt: 2 }}>
                {store.paymentLinks.length === 0 ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="10rem"
                    textAlign="center"
                  >
                    <Typography
                      variant="h6"
                      fontWeight="500"
                      color="text.secondary"
                    >
                      No data found
                    </Typography>
                  </Box>
                ) : (
                  <PaymentLinksTable
                    maxHeight={window.innerHeight - 340}
                    paymentLinks={store.paymentLinks}
                    limit={store.params.limit || store.limit}
                    page_no={store.params.page_no}
                    total_count={store.total_count}
                    sortBy={store.params.sort_by || "created_date"}
                    sortType={store.params.sort_type || SortType.DESC}
                    onPageChange={(_event, page) => store.setPage(page)}
                    onRowsPerPageChange={store.setRowsPerPage}
                    onSortChange={store.setSort}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Grid2>
  );
};

export default observer(PaymentLinksListing);
